import React, {useEffect} from "react";
import {observer} from "mobx-react";
import styled from "@emotion/styled";
import {IMatch} from "data/types/entities";
import {MatchStatus} from "views/components/match_status/match_status.component";
import {Button} from "@mui/material";
import {MatchUtils} from "data/utils/match";
import {useViewController} from "data/services/locator";
import {IMatchCardController} from "views/components/match_card/match_card.controller";
import {Bindings} from "data/constants/bindings";
import {MatchCardSquad} from "views/components/match_card/match_card_squad.component";
import {PossessionType} from "data/enums";
import {buildConnextraImpressionLink, ConnextraPlace} from "data/utils/connextra";

const Content = styled.div`
	margin-right: 10px;
	display: flex;
	flex-flow: column;
	padding: 8px;
	animation: fadeIn 1s ease forwards;
	opacity: 0;
	width: 100%;
	&:hover {
		background: rgba(18, 18, 18, 0.05);
	}
`;

const Buttons = styled.div`
	display: flex;
	justify-content: space-between;
	padding: 5px 0 0;
	font-size: 13px;
	img {
		opacity: 0;
		border: none;
		background: transparent;
	}
	p {
		padding: 8px 0;
		text-transform: uppercase;
		font-family: var(--fontFamilySecond);
		font-style: normal;
		font-weight: 500;
		line-height: 150%;
	}

	a {
		font-size: inherit;
		&:first-of-type {
			margin-right: auto;
		}
		&:last-of-type {
			margin-left: auto;
		}
	}

	@media (max-width: 768px) {
		font-size: 12px;
	}
`;

const Wrapper = styled.div`
	height: 100%;
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: flex-start;
	color: #000;
	border-radius: 2px;
	border-bottom: 1px solid rgba(18, 18, 18, 0.15);

	.caption {
		text-transform: uppercase;
	}

	img {
		max-width: 13px;
	}
`;

interface IProps {
	match: IMatch;
	selectedTournamentId?: number;
}

export const MatchCard: React.FC<IProps> = observer(({match, selectedTournamentId}) => {
	const controller = useViewController<IMatchCardController>(Bindings.MatchCardController);
	const className = match.id === selectedTournamentId ? "active" : "";

	useEffect(() => {
		if (match) {
			controller.setMatch(match);
		}
	}, [match, controller]);

	if (!match) {
		return null;
	}

	const {homeSquad, awaySquad} = match;

	return (
		<Content>
			<Wrapper className={`${className}`}>
				<MatchStatus match={match} />
				<MatchCardSquad
					squad={awaySquad}
					wdl={controller.wdlAway}
					timeouts={match.timeouts.away}
					odds={controller.getOdds(match.markets?.away)}
					className={controller.awayClass}
					match={match}
					isPossession={match.possession === PossessionType.AWAY}
				/>
				<MatchCardSquad
					squad={homeSquad}
					wdl={controller.wdlHome}
					timeouts={match.timeouts.home}
					odds={controller.getOdds(match.markets?.home)}
					className={controller.homeClass}
					match={match}
					isPossession={match.possession === PossessionType.HOME}
				/>
			</Wrapper>

			<Buttons>
				{MatchUtils.isScheduled(match) && (
					<Button href={controller.homeLink} variant={"text"} target={"_parent"}>
						<img
							src={buildConnextraImpressionLink(
								ConnextraPlace.Tickets,
								controller.i18n.locale
							)}
							width={"1"}
							height={"1"}
							alt={ConnextraPlace.Tickets}
						/>
						{controller.i18n.t("widget.card.tickets_button", "Tickets")}
					</Button>
				)}

				<Button href={controller.gameTrackerLink} variant={"text"} target={"_parent"}>
					<img
						src={buildConnextraImpressionLink(
							ConnextraPlace.GameTracker,
							controller.i18n.locale
						)}
						width={"1"}
						height={"1"}
						alt={ConnextraPlace.GameTracker}
					/>
					{controller.i18n.t("widget.card.gametracker_button", "Gametracker")}
				</Button>
			</Buttons>
		</Content>
	);
});
